import {
  AppBar,
  Box,
  Paper,
  Tab,
  Tabs,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { SnackbarContext } from 'components';
import * as React from 'react';
import {
  LightspeedMappingService,
  LightspeedShopId,
  LightspeedShopState,
  LightspeedSnapshotsForShop,
} from 'services';
import { ItemsTab } from './ItemsTab';
import { ModifiersTab } from './ModifiersTab';
import { PasteCatalogsTab } from './PasteCatalogsTab';
import { SetMenusTab } from './SetMenusTab';

type TabKey = 'paste' | 'items' | 'modifiers' | 'setMenus';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  shopBar: {
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    boxShadow: 'none',
    borderBottom: '1px solid ' + theme.palette.divider,
    marginBottom: theme.spacing(2),
  },
  shopLabel: {
    padding: theme.spacing(1.5, 2),
    fontWeight: 600,
  },
  tabPane: {
    padding: theme.spacing(2),
  },
}));

export const LightspeedMappingView: React.FC = () => {
  const classes = useStyles();
  const snackbar = React.useContext(SnackbarContext);

  const [shopId, setShopId] = React.useState<LightspeedShopId>(1);
  const [tab, setTab] = React.useState<TabKey>('paste');

  const [snapshots, setSnapshots] =
    React.useState<LightspeedSnapshotsForShop | null>(null);
  const [state, setState] = React.useState<LightspeedShopState | null>(null);
  const [loading, setLoading] = React.useState(false);

  const refresh = React.useCallback(async () => {
    try {
      setLoading(true);
      const [snaps, st] = await Promise.all([
        LightspeedMappingService.getSnapshots(shopId),
        LightspeedMappingService.getState(shopId),
      ]);
      setSnapshots(snaps);
      setState(st);
    } catch (e) {
      snackbar.error(e);
    } finally {
      setLoading(false);
    }
  }, [shopId, snackbar]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const itemsCatalog = snapshots?.itemsSnapshot?.parsed ?? [];
  const piCatalog =
    snapshots?.productionInstructionsSnapshot?.parsed ?? [];

  return (
    <Box className={classes.root}>
      <AppBar position="static" className={classes.shopBar}>
        <Box display="flex" alignItems="center">
          <Typography className={classes.shopLabel}>Shop:</Typography>
          <Tabs
            value={shopId}
            onChange={(_e, v) => setShopId(v as LightspeedShopId)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Street Food" value={1 as LightspeedShopId} />
            <Tab label="To Go" value={2 as LightspeedShopId} />
          </Tabs>
          <Box flex={1} />
          {loading && (
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ paddingRight: 16 }}
            >
              Loading…
            </Typography>
          )}
        </Box>
      </AppBar>

      <Paper>
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v as TabKey)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Paste catalogs" value="paste" />
          <Tab label="Items" value="items" />
          <Tab label="Modifiers" value="modifiers" />
          <Tab label="Set menus" value="setMenus" />
        </Tabs>

        <Box className={classes.tabPane}>
          {tab === 'paste' && (
            <PasteCatalogsTab
              shopId={shopId}
              snapshots={snapshots}
              onPasted={refresh}
            />
          )}
          {tab === 'items' && (
            <ItemsTab
              shopId={shopId}
              state={state}
              itemsCatalog={itemsCatalog}
              onChanged={refresh}
            />
          )}
          {tab === 'modifiers' && (
            <ModifiersTab
              shopId={shopId}
              state={state}
              piCatalog={piCatalog}
              onChanged={refresh}
            />
          )}
          {tab === 'setMenus' && (
            <SetMenusTab
              shopId={shopId}
              state={state}
              itemsCatalog={itemsCatalog}
              onChanged={refresh}
            />
          )}
        </Box>
      </Paper>
    </Box>
  );
};
